import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Chip,
  Collapse,
  Group,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { DateInput, TimeInput } from '@mantine/dates';
import { IconLink, IconMinus, IconPlus, IconTicket, IconBuilding, IconPlaneDeparture } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../../lib/api';
import type { CreateTodoInput, Todo, TodoDueInput, WeclappTicket } from '../../lib/api';
import type { ProjectCustomerRef } from '../../lib/api/domains/projects';
import { useAuthStore } from '../../stores/auth';
import { useActiveHub } from '../../hooks/useActiveHub';
import { WeclappTicketPicker } from '../../components/WeclappTicketPicker';
import { ProjectCustomerPicker } from '../projects/ProjectCustomerPicker';
import { parseGermanDate } from '../projects/projectLabels';
import { DUE_PRESETS, PRIORITY_MARK, TODO_QUERY_KEYS, untilLabel } from './todoLabels';

const RESOLVE_OPTIONS = [
  { value: 'MANUAL', label: 'Nur von Hand abhaken' },
  { value: 'CUSTOMER_REPLIED', label: 'Wenn der Kunde antwortet (Nachfassen)' },
  { value: 'TICKET_CLOSED', label: 'Wenn das Ticket geschlossen wird' },
];

export interface TodoFormDefaults {
  title?: string;
  notes?: string;
  links?: CreateTodoInput['links'];
  /** Anzeige der vorbelegten Verknüpfung, z. B. „T26-02243 · Drucker streikt" */
  linkLabel?: string;
  source?: 'MANUAL' | 'TICKET';
  due?: TodoDueInput | null;
  /** Nachfassen: erledigt sich von selbst, wenn … */
  resolveWhen?: 'CUSTOMER_REPLIED' | 'TICKET_CLOSED' | null;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Bearbeiten statt Anlegen */
  editing?: Todo | null;
  defaults?: TodoFormDefaults;
  onSaved?: (todo: Todo) => void;
  /** Über einem Popover/Drawer (Ticket-Cockpit) liegen */
  zIndex?: number;
}

/**
 * Anlegen und Bearbeiten. Techniker legen nur für sich selbst an — das Feld „Für" sehen nur
 * Geschäftsführung, Büro und Teamleitung. Aufgaben für andere bekommen die Eskalation (Standard
 * 4 Std. nach der Frist, mit „+" erhöhbar) und Abonnenten.
 */
export function TodoFormModal({ opened, onClose, editing, defaults, onSaved, zIndex = 300 }: Props) {
  const queryClient = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const { provider } = useActiveHub();
  const metaQuery = useQuery({ queryKey: [...TODO_QUERY_KEYS.all, 'meta'], queryFn: () => api.getTodoMeta(), staleTime: 5 * 60_000 });
  const meta = metaQuery.data;
  const isLead = meta?.isLead ?? (me?.role === 'ADMIN' || me?.role === 'TEAMLEAD');
  const peopleQuery = useQuery({
    queryKey: [...TODO_QUERY_KEYS.all, 'people'],
    queryFn: () => api.getTodoPeople(),
    enabled: opened && isLead,
    staleTime: 5 * 60_000,
  });

  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [time, setTime] = useState('');
  const [priority, setPriority] = useState(0);
  const [escalate, setEscalate] = useState<number | null>(240);
  const [subscriberIds, setSubscriberIds] = useState<string[]>([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [ticket, setTicket] = useState<WeclappTicket | null>(null);
  const [customer, setCustomer] = useState<ProjectCustomerRef | null>(null);
  const [resolveWhen, setResolveWhen] = useState<'CUSTOMER_REPLIED' | 'TICKET_CLOSED' | null>(null);

  // Beim Öffnen aus Vorgaben bzw. der bearbeiteten Aufgabe befüllen.
  useEffect(() => {
    if (!opened) return;
    setTitle(editing?.title ?? defaults?.title ?? '');
    setNotes(editing?.notes ?? defaults?.notes ?? '');
    setAssigneeId(editing?.assignee?.id ?? me?.id ?? null);
    const due = editing ? editing.due : defaults?.due ?? null;
    setDate(due?.date ?? null);
    setTime(due?.time ?? '');
    setPriority(editing?.priority ?? 0);
    setEscalate(editing ? editing.escalateAfterMinutes : meta?.defaultEscalateAfterMinutes ?? 240);
    setSubscriberIds([]);
    setLinkOpen(false);
    setTicket(null);
    setCustomer(null);
    setResolveWhen(
      editing
        ? editing.resolveWhen === 'CUSTOMER_REPLIED' || editing.resolveWhen === 'TICKET_CLOSED'
          ? editing.resolveWhen
          : null
        : (defaults?.resolveWhen ?? null),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, editing?.id]);

  const forOther = !!assigneeId && assigneeId !== (editing?.createdBy?.id ?? me?.id);
  const steps = meta?.escalationSteps ?? [{ minutes: 240, label: '4 Std.' }];
  const stepIndex = escalate == null ? -1 : steps.findIndex((s) => s.minutes === escalate);
  const escalateLabel =
    escalate == null ? null : (steps.find((s) => s.minutes === escalate)?.label ?? `${Math.round(escalate / 60)} Std.`);

  const people = peopleQuery.data?.users ?? [];
  const peopleOptions = useMemo(
    () => people.map((p) => ({ value: p.id, label: p.id === me?.id ? `${p.displayName} (ich)` : p.displayName })),
    [people, me?.id],
  );
  const assigneePerson = people.find((p) => p.id === assigneeId);
  // Nachfassen geht nur mit weclapp-Ticket — nur dort lässt sich eine Antwort erkennen.
  const hasWeclappTicket = editing
    ? editing.links.some((l) => l.kind === 'WECLAPP_TICKET')
    : !!defaults?.links?.weclappTicket || !!ticket;
  // Anruf- und Rückruf-Erinnerungen haben ihre eigene Bedingung — die bleibt unangetastet.
  const systemResolve =
    editing?.resolveWhen === 'CALLBACK_DONE' || editing?.resolveWhen === 'CUSTOMER_REACHED' || editing?.resolveWhen === 'REQUEST_ORDERED';

  const dueInput: TodoDueInput | null = date ? { date, time: time || null } : null;
  const activePreset = DUE_PRESETS.find((p) => {
    const v = p.get();
    return v.date === date && !time;
  })?.key;

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        // Nur Geändertes schicken: Eine neu gesetzte Frist setzt die Verschiebungen zurück,
        // eine geänderte Eskalation schaltet Stufen neu scharf — beides nicht beim bloßen Speichern.
        const dueChanged =
          (editing.due?.date ?? null) !== (dueInput?.date ?? null) ||
          (editing.due?.time ?? null) !== (dueInput?.time ?? null);
        return api.updateTodo(editing.id, {
          title: title.trim(),
          notes: notes.trim() || null,
          priority,
          ...(dueChanged ? { due: dueInput } : {}),
          ...(isLead && assigneeId && assigneeId !== editing.assignee?.id ? { assigneeUserId: assigneeId } : {}),
          ...((editing.kind === 'ASSIGNED' || forOther) && escalate !== editing.escalateAfterMinutes
            ? { escalateAfterMinutes: escalate }
            : {}),
          ...(!systemResolve && hasWeclappTicket && resolveWhen !== (editing.resolveWhen ?? null) ? { resolveWhen } : {}),
        });
      }
      const links: CreateTodoInput['links'] = defaults?.links ?? (ticket || customer
        ? {
            ...(ticket
              ? { weclappTicket: { id: ticket.id, number: ticket.ticketNumber, subject: ticket.subject, partyId: ticket.partyId ?? null, partyName: ticket.partyName ?? null } }
              : {}),
            ...(customer ? { customerId: customer.id } : {}),
          }
        : undefined);
      return api.createTodo({
        title: title.trim(),
        notes: notes.trim() || null,
        assigneeUserId: assigneeId ?? undefined,
        due: dueInput,
        priority,
        ...(forOther ? { escalateAfterMinutes: escalate, subscriberIds } : {}),
        links,
        source: defaults?.source ?? 'MANUAL',
        ...(hasWeclappTicket && resolveWhen ? { resolveWhen } : {}),
      });
    },
    onSuccess: ({ todo }) => {
      void queryClient.invalidateQueries({ queryKey: TODO_QUERY_KEYS.all });
      void queryClient.invalidateQueries({ queryKey: TODO_QUERY_KEYS.ticketCockpits });
      notifications.show({
        title: editing ? 'Gespeichert' : forOther ? 'Aufgabe vergeben' : 'Erinnerung angelegt',
        message: forOther && !editing && assigneePerson ? `${assigneePerson.displayName} bekommt Bescheid.` : '',
        color: 'green',
      });
      onSaved?.(todo);
      onClose();
    },
    onError: (e: Error) => notifications.show({ title: 'Nicht gespeichert', message: e.message, color: 'red' }),
  });

  const canSave = title.trim().length > 0 && !save.isPending && (!time || !!date);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={editing ? 'Bearbeiten' : isLead ? 'Neue Aufgabe oder Erinnerung' : 'Neue Erinnerung'}
      centered
      size="lg"
      zIndex={zIndex}
      closeOnClickOutside={false}
    >
      <Stack gap="sm">
        <TextInput
          label="Was ist zu tun?"
          placeholder="z. B. Müller zurückrufen wegen Drucker"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          maxLength={300}
          data-autofocus
          required
        />

        {defaults?.linkLabel && (
          <Badge variant="light" color="grape" leftSection={<IconLink size={12} />} style={{ alignSelf: 'flex-start', textTransform: 'none' }}>
            {defaults.linkLabel}
          </Badge>
        )}

        {isLead && (
          <Select
            label="Für"
            description={forOther ? 'Aufgabe — die Leitung erfährt vom Abhaken und von allem, was liegen bleibt' : 'Erinnerung für dich selbst'}
            data={peopleOptions}
            value={assigneeId}
            onChange={setAssigneeId}
            searchable
            allowDeselect={false}
            comboboxProps={{ zIndex: zIndex + 10 }}
            rightSection={
              assigneePerson && forOther && !assigneePerson.hasPush ? (
                <Tooltip label="Kein Push-Gerät — sieht die Aufgabe beim nächsten Besuch im ServiceHub" withinPortal zIndex={zIndex + 20}>
                  <Badge size="xs" color="yellow" variant="light">kein Push</Badge>
                </Tooltip>
              ) : undefined
            }
            rightSectionWidth={assigneePerson && forOther && !assigneePerson.hasPush ? 80 : undefined}
          />
        )}

        {forOther && assigneePerson?.absence && (
          <Alert color="orange" p="xs" icon={<IconPlaneDeparture size={16} />}>
            {assigneePerson.displayName} ist abwesend {untilLabel(assigneePerson.absence.until)} —{' '}
            {assigneePerson.absence.deputy
              ? `${assigneePerson.absence.deputy.displayName} vertritt und bekommt die Meldungen.`
              : 'ohne Vertretung, die Meldungen gehen an die Leitung.'}
          </Alert>
        )}

        <div>
          <Text size="sm" fw={500} mb={4}>
            Fällig
          </Text>
          <Group gap={6} mb={6}>
            {DUE_PRESETS.map((p) => (
              <Chip
                key={p.key}
                size="xs"
                checked={activePreset === p.key}
                onChange={() => {
                  const v = p.get();
                  setDate(v.date);
                  setTime('');
                }}
              >
                {p.label}
              </Chip>
            ))}
            <Chip size="xs" checked={!date} onChange={() => { setDate(null); setTime(''); }}>
              Ohne
            </Chip>
          </Group>
          <SimpleGrid cols={2}>
            <DateInput
              dateParser={parseGermanDate}
              placeholder="Datum"
              valueFormat="DD.MM.YYYY"
              clearable
              value={date}
              onChange={(v) => setDate(v)}
              popoverProps={{ zIndex: zIndex + 10 }}
            />
            <TimeInput
              placeholder="Uhrzeit (optional)"
              value={time}
              onChange={(e) => setTime(e.currentTarget.value)}
              disabled={!date}
            />
          </SimpleGrid>
          {!time && date && (
            <Text size="xs" c="dimmed" mt={4}>
              Ohne Uhrzeit: Erinnerung zu Bürobeginn, überfällig ab Büroschluss.
            </Text>
          )}
        </div>

        <Group gap="xs" align="center">
          <Text size="sm" fw={500}>
            Priorität
          </Text>
          <SegmentedControl
            size="xs"
            value={String(priority)}
            onChange={(v) => setPriority(Number(v))}
            data={PRIORITY_MARK.map((mark, i) => ({ value: String(i), label: mark || '–' }))}
          />
        </Group>

        {hasWeclappTicket && !systemResolve && (
          <Select
            label="Erledigt sich von selbst"
            description={
              resolveWhen === 'CUSTOMER_REPLIED'
                ? 'Antwortet der Kunde vorher (Mail oder Kundenportal) oder wird das Ticket geschlossen, entfällt die Erinnerung.'
                : resolveWhen === 'TICKET_CLOSED'
                  ? 'Wird das Ticket vorher geschlossen, entfällt die Erinnerung.'
                  : undefined
            }
            data={RESOLVE_OPTIONS}
            value={resolveWhen ?? 'MANUAL'}
            onChange={(v) => setResolveWhen(v === 'CUSTOMER_REPLIED' || v === 'TICKET_CLOSED' ? v : null)}
            allowDeselect={false}
            comboboxProps={{ zIndex: zIndex + 10 }}
          />
        )}

        {(forOther || editing?.kind === 'ASSIGNED') && (
          <Stack gap={4}>
            <Group gap="xs" align="center" wrap="nowrap">
              <Text size="sm" fw={500}>
                Leitung informieren
              </Text>
              {escalate != null ? (
                <>
                  <Tooltip label="Kürzer" withinPortal zIndex={zIndex + 20}>
                    <ActionIcon
                      variant="default"
                      size="sm"
                      disabled={stepIndex <= 0}
                      onClick={() => setEscalate(steps[Math.max(0, stepIndex - 1)].minutes)}
                      aria-label="Kürzer"
                    >
                      <IconMinus size={12} />
                    </ActionIcon>
                  </Tooltip>
                  <Badge variant="light" color="orange" size="lg" style={{ textTransform: 'none' }}>
                    {escalateLabel} überfällig
                  </Badge>
                  <Tooltip label="Länger" withinPortal zIndex={zIndex + 20}>
                    <ActionIcon
                      variant="default"
                      size="sm"
                      disabled={stepIndex >= steps.length - 1}
                      onClick={() => setEscalate(steps[Math.min(steps.length - 1, stepIndex + 1)].minutes)}
                      aria-label="Länger"
                    >
                      <IconPlus size={12} />
                    </ActionIcon>
                  </Tooltip>
                </>
              ) : (
                <Badge variant="light" color="gray" size="lg" style={{ textTransform: 'none' }}>
                  nie
                </Badge>
              )}
              <Switch
                size="xs"
                ml="auto"
                label="Keine Eskalation"
                checked={escalate == null}
                onChange={(e) => {
                  const __checked = e.currentTarget.checked;
                  setEscalate(__checked ? null : (meta?.defaultEscalateAfterMinutes ?? 240));
                }}
              />
            </Group>
            <Text size="xs" c="dimmed">
              {date ? 'Ab der Fälligkeit gezählt' : 'Ohne Fälligkeit ab jetzt gezählt'}, in Bürozeit. Nicht angesehen nach 2 Std.
              bekommt {assigneePerson?.displayName ?? 'der Zuständige'} eine zweite Erinnerung, einen Bürotag später meldet sich die
              Aufgabe noch einmal bei der Leitung.
            </Text>
          </Stack>
        )}

        {forOther && !editing && isLead && (
          <MultiSelect
            label="Abonnenten"
            description="Bekommen Bescheid beim Abhaken und wenn es liegen bleibt — zusätzlich zur Leitung"
            data={peopleOptions.filter((o) => o.value !== assigneeId)}
            value={subscriberIds}
            onChange={setSubscriberIds}
            searchable
            clearable
            comboboxProps={{ zIndex: zIndex + 10 }}
          />
        )}

        <Textarea
          label="Notiz"
          placeholder="Details, Telefonnummer, worauf zu achten ist …"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          autosize
          minRows={2}
          maxRows={6}
          maxLength={5000}
        />

        {!editing && !defaults?.links && (
          <>
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={<IconLink size={14} />}
              onClick={() => setLinkOpen((o) => !o)}
              style={{ alignSelf: 'flex-start' }}
            >
              {ticket || customer ? 'Verknüpfung ändern' : 'Mit Ticket oder Kunde verknüpfen (optional)'}
            </Button>
            <Collapse expanded={linkOpen}>
              <Stack gap="xs">
                <ProjectCustomerPicker value={customer} onChange={setCustomer} label="Kunde" />
                {provider === 'weclapp' && (
                  <div>
                    <Text size="sm" fw={500} mb={4}>
                      Ticket
                    </Text>
                    <WeclappTicketPicker
                      value={ticket?.id}
                      selectedTicketData={ticket ?? undefined}
                      onChange={(_id, t) => setTicket(t ?? null)}
                    />
                  </div>
                )}
              </Stack>
            </Collapse>
            {!linkOpen && (ticket || customer) && (
              <Group gap={6}>
                {ticket && (
                  <Badge variant="light" leftSection={<IconTicket size={12} />} style={{ textTransform: 'none' }}>
                    {ticket.ticketNumber} · {ticket.subject}
                  </Badge>
                )}
                {customer && (
                  <Badge variant="light" color="teal" leftSection={<IconBuilding size={12} />} style={{ textTransform: 'none' }}>
                    {customer.name}
                  </Badge>
                )}
              </Group>
            )}
          </>
        )}

        {time && !date && (
          <Alert color="yellow" p="xs">
            Für eine Uhrzeit bitte auch ein Datum wählen.
          </Alert>
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!canSave}>
            {editing ? 'Speichern' : forOther ? 'Aufgabe vergeben' : 'Anlegen'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
